import { useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { GuideEvent, GuideImage } from "@shared/epg-guide";
import { imageSrc } from "@shared/vod-images";
import { useScheme } from "../schemeContext";
import { useNow } from "../useNow";
import { useTvLineup, type TvLineupChannel } from "../../../hooks/useTvLineup";
import {
  useTvCatalogue,
  type TvCatalogueItem,
  type TvLocale,
} from "../../../hooks/useTvCatalogue";
import { eventClock, nowNext, progressOf, tunable } from "../guide/lineup";
import type { VodScreenStyle } from "../vod/VodScreen";

/**
 * The home of the built-in player: one hero, then carousels (`DEMO-3`).
 *
 * ## The hero follows FOCUS, and that is the whole design
 *
 * Read off the app being replaced. Moving along the channel rail repaints the
 * hero with what is on that channel now, its synopsis, and the three programmes
 * after it; moving onto a film repaints it with the film. The guest browses the
 * hero, not the tiles - which is why the tiles can stay small enough to fit six
 * on a row, and why a channel needs no separate "info" press.
 *
 * It also means the hero must never lag the ring. A hero fed from a fetch that
 * fires per focus change would trail the remote by a frame on a good uplink and
 * by a second on a hotel one, and the guest would watch it catch up. So
 * everything the hero draws comes from data already in hand: the line-up
 * carries its own events, and `nowNext()` is a pure read against the device
 * clock.
 *
 * ## Two rails, from two different ideas of "selected"
 *
 * - **Kanaler** is the room's ENTITLEMENT - which channels this room gets, in
 *   the order the line-up states.
 * - Everything under it is the PREMISE's selection - `carouselsForRoom`, the
 *   rails an operator authored and ordered in the CMS.
 *
 * They are deliberately not merged into one list. A channel is a place you go
 * and stay; a film is a thing you pick. Ranking them together would make the
 * first row of the home page depend on how many films happen to be licensed
 * this month.
 *
 * ## What a channel card shows, and why it is not a poster
 *
 * A white card with the channel's own logo, the programme on now along the
 * foot, and a progress bar. Channel identity is a LOGO - a guest looking for
 * SVT1 is looking for the SVT1 mark, not for a word in a list - and the
 * progress bar answers "have I missed it?" without a second press. Both are
 * copied straight from the app this replaces, on the same reasoning as the
 * rail: an operator comparing the two should not have to relearn anything.
 */

/**
 * How many tiles of a rail the home draws.
 *
 * A cap rather than the whole set, because the home is a shop window and a
 * hundred-tile rail costs a hundred images on a panel with a single-digit
 * decoder pool and no spare memory. The full rail lives on the `vod` screen,
 * which pages properly.
 */
const RAIL_MAX = 20;

export interface TvHomeProps {
  style: VodScreenStyle;
  scopeRef: RefObject<HTMLElement>;
  /** Leave the home - the shell puts focus back on its menu. */
  onBack: () => void;
  /** Tune a channel. Absent while live playback is still being built. */
  onTune?: (channel: TvLineupChannel) => void;
  /** Open a title. Absent until the detail page is reachable from here. */
  onOpen?: (item: TvCatalogueItem) => void;
}

interface Row {
  key: string;
  title: string;
  kind: "channels" | "vod";
  channels?: TvLineupChannel[];
  items?: TvCatalogueItem[];
}

export default function TvHome({
  style,
  scopeRef,
  onBack,
  onTune,
  onOpen,
}: TvHomeProps) {
  const scheme = useScheme();
  const nowDate = useNow();
  const now = nowDate.getTime();
  const loc: TvLocale = useMemo(
    () => ({
      locale: scheme.context.locale ?? "en",
      defaultLocale:
        scheme.context.defaultLocale ?? scheme.context.locale ?? "en",
    }),
    [scheme.context.locale, scheme.context.defaultLocale]
  );
  // Both feeds come from the router, which already fetched them to decide this
  // section exists at all. Asking again here would poll twice for one answer
  // and, in any surface where the router holds the data but a television has no
  // binding - the design preview above all - would draw an empty page under a
  // menu that says there is something there.
  //
  // 🚨 So the reads are switched OFF here, not merely handed a preview. Passing
  // the router's value alone only short-circuits when that value is DEFINED:
  // a premise with the guide on and VOD off has `scheme.vod === undefined`, and
  // this component would quietly start its own ten-minute poll of `/tv/home`
  // for a feed the premise never asked for. That is the ungated read
  // `tvFeeds.ts` records as having shipped twice already - it is how `showCast`
  // switched Google Cast on in care rooms. `enabled: false` and an absent
  // premise id make this a pure consumer of the router's copy.
  const lineup = useTvLineup(scheme.guide, false);
  const { home } = useTvCatalogue(undefined, loc, scheme.vod);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    const channels = (lineup?.channels ?? []).filter(tunable);
    if (channels.length > 0) {
      out.push({
        key: "channels",
        title: "Kanaler",
        kind: "channels",
        channels,
      });
    }
    for (const rail of home?.rails ?? []) {
      // A rail whose first page came back empty is UNAVAILABLE, and a named
      // heading with a void under it is worse than no heading at all.
      if (rail.items.length === 0) continue;
      out.push({
        key: rail.carouselId,
        title: rail.title,
        kind: "vod",
        items: rail.items,
      });
    }
    return out;
  }, [lineup, home]);

  const [pos, setPos] = useState({ r: 0, c: 0 });
  // Ragged, not a grid: rails have different lengths, and a fixed-width grid
  // would let the ring land on a cell that is not there. Clamping on read
  // rather than on write keeps the COLUMN when moving between a long rail and a
  // short one and back - the behaviour a guest expects from every ten-foot UI.
  const lengths = rows.map((r) =>
    r.kind === "channels" ? (r.channels?.length ?? 0) : (r.items?.length ?? 0)
  );
  const r = Math.min(pos.r, Math.max(0, rows.length - 1));
  const c = Math.min(pos.c, Math.max(0, (lengths[r] ?? 1) - 1));

  const handlers = useRef({ onBack, onTune, onOpen, rows, r, c });
  useEffect(() => {
    handlers.current = { onBack, onTune, onOpen, rows, r, c };
  });

  useEffect(() => {
    const el = scopeRef.current;
    if (!el) return;
    function onKey(e: KeyboardEvent) {
      const node = scopeRef.current;
      if (
        !node ||
        (!node.contains(document.activeElement) &&
          document.activeElement !== node)
      ) {
        return;
      }
      const h = handlers.current;
      const rowCount = h.rows.length;
      if (rowCount === 0) return;
      // 🚨 Clamped to what is actually RENDERED, not to what the feed holds.
      // Each rail is sliced to `RAIL_MAX` when drawn, while a real line-up runs
      // to `TV_LINEUP_MAX_CHANNELS` (80). Measuring the full array let the
      // highlight walk off the end of the row: no card carries `data-focused`,
      // the ring vanishes, `scrollIntoView` finds nothing, and the page looks
      // frozen — while OK tunes a channel that was never on screen.
      const len = Math.min(
        h.rows[h.r]?.kind === "channels"
          ? (h.rows[h.r]?.channels?.length ?? 0)
          : (h.rows[h.r]?.items?.length ?? 0),
        RAIL_MAX
      );
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setPos({ r: Math.min(h.r + 1, rowCount - 1), c: h.c });
          break;
        case "ArrowUp":
          e.preventDefault();
          // Up is for moving BETWEEN rails and nothing else. It used to leave
          // for the menu at the top row, which meant a guest scrolling back up
          // through the page overshot straight out of it - the menu is to the
          // LEFT, and that is the only direction that should reach it.
          if (h.r > 0) setPos({ r: h.r - 1, c: h.c });
          break;
        case "ArrowRight":
          e.preventDefault();
          setPos({ r: h.r, c: Math.min(h.c + 1, Math.max(0, len - 1)) });
          break;
        case "ArrowLeft":
          e.preventDefault();
          // LEFT IS THE WAY BACK TO THE MENU, and the only one: the menu is a
          // rail down the left-hand edge, so walking off the left of the page
          // is walking into it. Within a rail it steps between tiles first;
          // from the first tile it leaves.
          if (h.c > 0) setPos({ r: h.r, c: h.c - 1 });
          else h.onBack();
          break;
        case "Enter":
        case " ": {
          e.preventDefault();
          const row = h.rows[h.r];
          if (!row) return;
          if (row.kind === "channels") {
            const ch = row.channels?.[h.c];
            if (ch) h.onTune?.(ch);
          } else {
            const item = row.items?.[h.c];
            if (item) h.onOpen?.(item);
          }
          break;
        }
        case "Backspace":
        case "Escape":
          e.preventDefault();
          h.onBack();
          break;
        default:
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scopeRef]);

  /**
   * Keep the ring on screen.
   *
   * Driven off the DOM's own `data-focused` mark rather than a second index
   * kept in step by hand: there is one source of truth for where the highlight
   * is, and this reads it. `block: "nearest"` scrolls the minimum that brings
   * the row into view, so a small move does not throw the page.
   *
   * ⚠️ `scrollIntoView` is optional-called on purpose. jsdom does not implement
   * it, and a hard call here would fail every test that presses an arrow -
   * turning a scrolling bug into a suite-wide crash.
   */
  useEffect(() => {
    const el = scopeRef.current?.querySelector<HTMLElement>(
      '[data-focused="true"]'
    );
    el?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [r, c, scopeRef]);

  const focusedRow = rows[r];
  const focusedChannel =
    focusedRow?.kind === "channels" ? focusedRow.channels?.[c] : undefined;
  const focusedItem =
    focusedRow?.kind === "vod" ? focusedRow.items?.[c] : undefined;

  const nn = focusedChannel ? nowNext(focusedChannel, now) : null;

  if (rows.length === 0) {
    return (
      <div
        data-testid="tv-home-empty"
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          color: style.inkDim,
          font: `500 28px ${style.font}`,
        }}
      >
        Inget att visa i det här rummet ännu.
      </div>
    );
  }

  return (
    <div
      data-testid="tv-home"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        color: style.ink,
        font: `400 24px ${style.font}`,
        overflow: "hidden",
      }}
    >
      <Hero
        style={style}
        channel={focusedChannel}
        nn={nn}
        item={focusedItem}
        now={now}
      />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          // The rails SCROLL. Without this the ring walks down past the fold
          // and the guest watches the highlight leave the screen while the
          // page sits still - which reads as the remote having stopped
          // working, because nothing moves.
          //
          // `auto`, and the bar is hidden below: a television has no pointer to
          // drag one with, so a scrollbar is decoration that eats a column.
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          // Top padding, and a scroll margin on each row, so the focus ring on
          // the FIRST rail is not sliced off by the hero above it. The ring is
          // drawn outside the tile, and `scrollIntoView({block:"nearest"})`
          // stops at the container edge - which is exactly where the ring is.
          padding: "24px 0 40px 48px",
        }}
      >
        {rows.map((row, ri) => (
          <div
            key={row.key}
            style={{
              marginBottom: 28,
              scrollMarginTop: 16,
              scrollMarginBottom: 24,
            }}
          >
            <div
              data-testid={`tv-home-rail-${row.key}`}
              style={{
                font: `600 26px ${style.font}`,
                color: style.inkDim,
                marginBottom: 12,
              }}
            >
              {row.title}
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
                // Each rail scrolls sideways on its own, so a long line-up is
                // not capped by the width of the panel.
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                // Room for the focus ring, which is drawn outside the card and
                // would otherwise be clipped by this box on the first and last
                // tile.
                padding: "6px 48px 6px 6px",
                margin: "-6px 0 -6px -6px",
              }}
            >
              {row.kind === "channels"
                ? row.channels
                    ?.slice(0, RAIL_MAX)
                    .map((ch, ci) => (
                      <ChannelCard
                        key={ch.id}
                        style={style}
                        channel={ch}
                        now={now}
                        focused={ri === r && ci === c}
                      />
                    ))
                : row.items
                    ?.slice(0, RAIL_MAX)
                    .map((it, ci) => (
                      <PosterCard
                        key={it.assetId}
                        style={style}
                        item={it}
                        focused={ri === r && ci === c}
                      />
                    ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The picture behind the hero.
 *
 * `GuideEvent.image` is a `GuideImage` OBJECT, not a URL - and it holds the
 * path exactly as upstream stores it, container-relative, because `BE-9`
 * deleted the absolutise-on-read and made every Distribution surface compose
 * its own through the image scaler. Interpolating the object straight into a
 * `url(...)` renders the string "[object Object]" and paints nothing, which is
 * precisely what it did.
 *
 * Absolute and `data:` URLs pass through untouched: the first is a real remote
 * image, the second is a design fixture, and neither is the scaler's business.
 */
function heroArt(
  image: GuideImage | null | undefined,
  fallback?: string
): string | undefined {
  const url = image?.url;
  if (!url) return fallback;
  if (/^(https?:|data:)/i.test(url)) return url;
  return imageSrc({ url }, { width: 1280 }) ?? fallback;
}

function Hero({
  style,
  channel,
  nn,
  item,
  now,
}: {
  style: VodScreenStyle;
  channel?: TvLineupChannel;
  nn: ReturnType<typeof nowNext> | null;
  item?: TvCatalogueItem;
  now: number;
}) {
  const current = nn?.current ?? null;
  const upcoming = useMemo(() => {
    if (!channel) return [] as GuideEvent[];
    const at = now;
    return channel.events
      .filter((e) => {
        const s = Date.parse(e.startScheduled);
        return Number.isFinite(s) && s > at;
      })
      .slice(0, 3);
  }, [channel, now]);

  const art = heroArt(current?.image, item?.imageUrl);
  const title = current?.title ?? item?.title ?? channel?.title ?? "";
  const synopsis = current?.synopsis ?? "";
  const when = current ? eventClock(current) : "";

  return (
    <div
      data-testid="tv-home-hero"
      style={{
        position: "relative",
        height: "40%",
        flex: "0 0 40%",
        padding: "56px 48px 0 48px",
        overflow: "hidden",
      }}
    >
      {/* Artwork sits behind, faded out toward the text. A television's hero is
          a photograph with words on it, and words on a photograph need the
          photograph to get out of their way on the reading side. */}
      {art && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            // Quoted: an unquoted `url()` is broken by a parenthesis, a space or
            // a quote in the URL, and a catalogue is full of all three.
            backgroundImage: `url("${art}")`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
            maskImage:
              "linear-gradient(to right, transparent 0%, transparent 30%, black 75%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, transparent 30%, black 75%)",
          }}
        />
      )}
      <div style={{ position: "relative", maxWidth: "52%" }}>
        <div
          data-testid="tv-home-hero-title"
          style={{
            font: `700 56px/1.15 ${style.titleFont}`,
            // 1.15 rather than 1.0: a tight line-height with the overflow this
            // box needs clips descenders on a television, and "Friidrotts-EM"
            // is exactly the shape that shows it.
            marginBottom: 8,
          }}
        >
          {title}
        </div>
        {when && (
          <div style={{ color: style.inkDim, font: `400 26px ${style.font}` }}>
            {when}
          </div>
        )}
        {synopsis && (
          <div
            style={{
              marginTop: 20,
              color: style.ink,
              font: `400 26px/1.45 ${style.font}`,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {synopsis}
          </div>
        )}
        {upcoming.length > 0 && (
          <div style={{ marginTop: 24, display: "grid", gap: 6 }}>
            {upcoming.map((e) => (
              <div
                key={e.epgEventId}
                style={{
                  display: "flex",
                  gap: 24,
                  color: style.inkDim,
                  font: `400 24px ${style.font}`,
                }}
              >
                <span style={{ minWidth: 150 }}>{eventClock(e)}</span>
                <span style={{ color: style.ink }}>{e.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChannelCard({
  style,
  channel,
  now,
  focused,
}: {
  style: VodScreenStyle;
  channel: TvLineupChannel;
  now: number;
  focused: boolean;
}) {
  const nn = nowNext(channel, now);
  const pct = nn.current ? progressOf(nn.current, now) : 0;
  return (
    <div
      data-testid={`tv-channel-${channel.id}`}
      data-focused={focused ? "true" : undefined}
      style={{
        width: 300,
        height: 170,
        borderRadius: style.radius,
        // White, whatever the template's ground is. A channel logo is a brand
        // mark supplied on white and it stops being recognisable on anything
        // else - the one place a template does NOT get to impose its palette.
        background: "#fff",
        position: "relative",
        overflow: "hidden",
        outline: focused ? `4px solid ${style.accent}` : "none",
        outlineOffset: 2,
        flex: "0 0 auto",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: "0 0 46px 0",
          display: "grid",
          placeItems: "center",
          padding: 24,
        }}
      >
        {channel.logoUrl ? (
          <img
            src={channel.logoUrl}
            alt={channel.title}
            style={{ maxWidth: "70%", maxHeight: "70%", objectFit: "contain" }}
          />
        ) : (
          <div style={{ color: "#111", font: `700 34px ${style.titleFont}` }}>
            {channel.title}
          </div>
        )}
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 46,
          background: "rgba(0,0,0,0.82)",
          color: "#fff",
          font: `600 20px/46px ${style.font}`,
          padding: "0 12px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {nn.state === "live" && nn.current
          ? nn.current.title
          : nn.state === "gap"
            ? "Ingen information just nu"
            : "Ingen tablå"}
      </div>
      {/* The progress bar answers "have I missed it?" without a second press. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          height: 4,
          width: `${Math.round(pct * 100)}%`,
          background: style.accent,
        }}
      />
    </div>
  );
}

function PosterCard({
  style,
  item,
  focused,
}: {
  style: VodScreenStyle;
  item: TvCatalogueItem;
  focused: boolean;
}) {
  return (
    <div
      data-testid={`tv-title-${item.assetId}`}
      data-focused={focused ? "true" : undefined}
      style={{
        width: 200,
        height: 170,
        borderRadius: style.radius,
        background: item.imageUrl ? "transparent" : style.placeholder,
        backgroundImage: item.imageUrl ? `url("${item.imageUrl}")` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        outline: focused ? `4px solid ${style.accent}` : "none",
        outlineOffset: 2,
        flex: "0 0 auto",
        display: "flex",
        alignItems: "flex-end",
        overflow: "hidden",
      }}
    >
      {!item.imageUrl && (
        <div
          style={{
            padding: 12,
            font: `600 22px/1.25 ${style.font}`,
            color: style.ink,
            // 1.25, not 1.0. Descenders on a poster caption are the exact
            // shape `tv-tight-line-height-clipping` is about.
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          {item.title}
        </div>
      )}
    </div>
  );
}
